import { sortBy } from "@common/helpers/collections";
import { FINANCIAL_ACCOUNT_TYPE_LABEL } from "../constants";
import {
    FinancialAccount,
    FinancialAccountBase,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "../types/financialAccount";
import { FinancialConnection } from "../types/financialConnection";
import { IntegrationAccountBase } from "../types/integrationAccount";
import { IntegrationConnection } from "../types/integrationConnection";
import { getAnonymousAccountNumber } from "./bankAccount";

export const ACCOUNTS_POSSIBLY_PERSONAL: Record<
    FinancialAccountSourceType,
    boolean
> = {
    [FinancialAccountSourceType.FILE_IMPORT]: true,
    [FinancialAccountSourceType.PLAID]: true,
    [FinancialAccountSourceType.STRIPE]: false,
    [FinancialAccountSourceType.GUSTO]: false,
    [FinancialAccountSourceType.ADP_RUN]: false,
    [FinancialAccountSourceType.MERCURY]: true,
    [FinancialAccountSourceType.RAMP]: false,
    [FinancialAccountSourceType.PAYPAL]: true,
    [FinancialAccountSourceType.MANUAL]: true,
    [FinancialAccountSourceType.BILLCOM]: false,
    [FinancialAccountSourceType.CHECK]: true,
    [FinancialAccountSourceType.DEPOSIT]: true,
} as const;

export function buildAccountDisplayName(
    accountName: string,
    accountNumberMask: string | null,
): string {
    if (accountNumberMask) {
        return `${accountName} ${getAnonymousAccountNumber(accountNumberMask, 2, "")}`;
    }
    return accountName;
}

export function getAccountNameWithLast4(
    account: Pick<FinancialAccount, "name" | "accountNumberMask">,
    accountNumberMaskLength = 4,
) {
    const name = account.name ?? "";

    if (/\d{4}$/.test(name) || !account.accountNumberMask) {
        return name;
    } else {
        return `${name} ${getAnonymousAccountNumber(
            account.accountNumberMask,
            accountNumberMaskLength,
        )}`;
    }
}

export function getAccountNameForConfirmation(account: FinancialAccountBase) {
    const name = account.name ?? "";

    if (/\d{4}$/.test(name) || !account.accountNumberMask) {
        return name;
    } else {
        const last4Digits = account.accountNumberMask.substring(
            account.accountNumberMask.length - 4,
        );

        return `${name} ${last4Digits}`;
    }
}

export function checkIfEnteredAccountNameIsValidForConfirmation(
    accountName: string,
    account: FinancialAccountBase,
) {
    const replacedPart = /\s/g;
    return (
        accountName.replace(replacedPart, "").toLowerCase() ===
        getAccountNameForConfirmation(account)
            .replace(replacedPart, "")
            .toLowerCase()
    );
}

const FINANCIAL_ACCOUNT_TYPES = new Set<string>(
    Object.values(FinancialAccountType),
);

function isFinancialAccountType(value: string): value is FinancialAccountType {
    return FINANCIAL_ACCOUNT_TYPES.has(value);
}

export const FINANCIAL_TYPES_AVAILABLE_FOR_STATEMENT_RETRIEVAL = [
    FinancialAccountType.CHECKING,
    FinancialAccountType.SAVINGS,
    FinancialAccountType.CREDIT_CARD,
    FinancialAccountType.PAYMENT_PROCESSOR,
] as const;

export type StatementRetrievalFinancialAccountType =
    (typeof FINANCIAL_TYPES_AVAILABLE_FOR_STATEMENT_RETRIEVAL)[number];

export function isStatementRetrievalFinancialAccountType(
    value: string,
): value is StatementRetrievalFinancialAccountType {
    return FINANCIAL_TYPES_AVAILABLE_FOR_STATEMENT_RETRIEVAL.some(
        (type) => type === value,
    );
}

/**
 * Display label for an account type that arrives as a plain string — API
 * payloads type it as `string` even though it always holds a
 * `FinancialAccountType`. Returns `null` for a missing or unrecognised value so
 * callers pick their own placeholder instead of leaking `credit_card`.
 */
export function financialAccountTypeLabel(type: string | null): string | null {
    if (type === null || !isFinancialAccountType(type)) {
        return null;
    }
    return FINANCIAL_ACCOUNT_TYPE_LABEL[type];
}

export function getAccountShortName(account: FinancialAccount) {
    switch (account.sourceType) {
        case FinancialAccountSourceType.PLAID:
            return FINANCIAL_ACCOUNT_TYPE_LABEL[account.financialType];
        case FinancialAccountSourceType.STRIPE:
            return "Stripe";
        case FinancialAccountSourceType.GUSTO:
            return "Gusto";
        case FinancialAccountSourceType.ADP_RUN:
            return "ADP RUN";
        default:
            return account.name;
    }
}

export function isCardAccount(account: FinancialAccount) {
    return account.financialType === FinancialAccountType.CREDIT_CARD;
}

export function isLiabilityAccount(
    financialType: FinancialAccountType,
): boolean {
    return (
        financialType === FinancialAccountType.LOAN ||
        financialType === FinancialAccountType.LINE_OF_CREDIT ||
        financialType === FinancialAccountType.CREDIT_CARD
    );
}

export function getMatchingIntegrationAccount(
    account: FinancialAccountBase,
): IntegrationAccountBase | undefined {
    const matching = (account.integrationAccounts ?? []).filter(
        (integrationAccount) =>
            integrationAccount.sourceType === account.sourceType &&
            integrationAccount.sourceAccountId,
    );

    return matching[0];
}

export function getLeadingAccountConnection(
    account: FinancialAccountBase,
): FinancialConnection | IntegrationConnection | null {
    if (!account.integrationAccounts) {
        return null;
    }

    const connectionsWithSourceType = account.integrationAccounts
        .map((ia) => ({
            connection: ia.connection ?? ia.integrationConnection,
            sourceType: ia.sourceType,
        }))
        .filter(
            (
                entry,
            ): entry is typeof entry & {
                connection: NonNullable<typeof entry.connection>;
            } => entry.connection != null,
        );

    if (!connectionsWithSourceType.length) {
        return null;
    }

    const pickOldest = (
        entries: typeof connectionsWithSourceType,
    ): FinancialConnection | IntegrationConnection =>
        sortBy(entries, (e) => new Date(e.connection.createdAt).getTime())[0]
            .connection as FinancialConnection | IntegrationConnection;

    const healthy = connectionsWithSourceType.filter(
        (e) => !e.connection.error,
    );

    if (healthy.length) {
        const matchingSourceType = healthy.filter(
            (e) => e.sourceType === account.sourceType,
        );
        return pickOldest(
            matchingSourceType.length ? matchingSourceType : healthy,
        );
    }

    return pickOldest(connectionsWithSourceType);
}
